import type { ChangeEvent, FocusEvent, MouseEvent } from 'react';
import { useSingleExecutionFunction } from '@noths/polaris-client-react-hooks';

import * as track from './tracking';

interface EventActionParams {
  action: string;
  event: FocusEvent<HTMLInputElement | HTMLSelectElement> | ChangeEvent<HTMLInputElement>;
  required?: boolean;
}

export const useTracking = () => {
  const trackFocusOnce = useSingleExecutionFunction(
    (event: FocusEvent<HTMLInputElement | HTMLSelectElement>) => trackFocus(event),
  );

  const trackFocus = (event: FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { target } = event;
    const { dataset, name, type } = target;
    const { position, section } = dataset;
    if (section && position) {
      const required = target.required || target.getAttribute('aria-required') === 'true';

      track.event({
        action: `${type} Field Focus`,
        label: name,
        fieldDetails: {
          form_section: `Section ${section}`,
          field_position: position,
          field_type: type,
          field_name: name,
          required_field: required,
        },
      });
    }
  };

  const trackCompleted = (
    event: FocusEvent<HTMLInputElement | HTMLSelectElement> | ChangeEvent<HTMLInputElement>,
  ) => {
    const { target } = event;
    const { dataset, name, type } = target;
    const { position, section } = dataset;

    if (section && position) {
      const isAriaValid =
        !target.hasAttribute('aria-invalid') || target.getAttribute('aria-invalid') === 'false';
      const isValid = target.validity.valid && isAriaValid;

      if (isValid) {
        const required = target.required || target.getAttribute('aria-required') === 'true';

        track.event({
          action: `${type} Field ${type === 'text' ? 'Input' : 'Selection'}`,
          label: name,
          fieldDetails: {
            form_section: `Section ${section}`,
            field_position: position,
            field_type: type,
            field_name: name,
            required_field: required,
          },
        });
      }
    }
  };

  const trackAction = ({ action, event, required = false }: EventActionParams) => {
    const { target } = event;
    const { dataset, name, type } = target;
    const { position, section } = dataset;
    if (section && position) {
      track.event({
        action,
        label: name,
        fieldDetails: {
          form_section: `Section ${section}`,
          field_position: position,
          field_type: type,
          field_name: name,
          required_field: required,
        },
      });
    }
  };

  const trackClick = (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    const { currentTarget } = event;
    const { dataset, textContent: text, type } = currentTarget;
    const { position, section } = dataset;
    if (section && position) {
      track.event({
        action: `${text} Click`,
        label: text || '',
        fieldDetails: {
          form_section: `Section ${section}`,
          field_position: position,
          field_type: type,
        },
      });
    }
  };

  return { trackFocusOnce, trackCompleted, trackAction, trackClick, trackEvent: track.event };
};
